import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { accountService, type AccountDoc } from '../lib/account';
import type { PlanEntitlements } from '../lib/billing/types';

type AccountContextValue = {
  account: AccountDoc | null;
  planId: string;
  entitlements: PlanEntitlements | null;
  loading: boolean;
  refresh: () => Promise<void>;
  ensureAccount: () => Promise<AccountDoc | null>;
};

const AccountContext = createContext<AccountContextValue | null>(null);

export function AccountProvider({ children }: { children: React.ReactNode }) {
  const { user, userData } = useAuth();
  const [account, setAccount] = useState<AccountDoc | null>(null);
  const [entitlements, setEntitlements] = useState<PlanEntitlements | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user?.uid) {
      setAccount(null);
      setEntitlements(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let acct = await accountService.getAccountForUser(user.uid);
      if (!acct && userData) {
        try {
          acct = await accountService.ensureAccountForUser(
            user.uid,
            userData as Record<string, unknown>,
          );
        } catch (err) {
          console.warn('[AccountContext] ensureAccountForUser failed:', err);
        }
      }
      setAccount(acct);
      if (acct) {
        try {
          setEntitlements(await accountService.getAccountEntitlements(acct));
        } catch (err) {
          console.warn('[AccountContext] getAccountEntitlements failed:', err);
          setEntitlements(null);
        }
      } else {
        setEntitlements(null);
      }
    } catch (err) {
      console.warn('[AccountContext] refresh failed:', err);
      setAccount(null);
      setEntitlements(null);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, userData]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const ensureAccount = useCallback(async () => {
    if (!user?.uid || !userData) return null;
    const acct = await accountService.ensureAccountForUser(
      user.uid,
      userData as Record<string, unknown>,
    );
    setAccount(acct);
    setEntitlements(await accountService.getAccountEntitlements(acct));
    return acct;
  }, [user?.uid, userData]);

  const planId = useMemo(() => {
    if (account) return accountService.resolveAccountPlanId(account);
    return 'free';
  }, [account]);

  const value = useMemo(
    () => ({ account, planId, entitlements, loading, refresh, ensureAccount }),
    [account, planId, entitlements, loading, refresh, ensureAccount],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error('useAccount must be used within AccountProvider');
  return ctx;
}

export function useAccountOptional() {
  return useContext(AccountContext);
}
